/**
 * useCuratedExamples — fetch the admin-curated "Try [Species]" presets for
 * a given module. Returns the same shape the legacy hardcoded
 * `moduleExamples.js` exports used to (species_label / badge_color /
 * inputs / monthly_temperatures), so the page consumers don't need to
 * care that the source has moved server-side.
 *
 * Data flow:
 *   moduleExamples.js (deleted)  ─┐
 *                                 ├─►  GET /api/curated-examples
 *   AdminConsole picks per-cell  ─┘    (seeded from in-code defaults on
 *                                      first boot; admin overrides via
 *                                      /api/admin/curated-examples)
 */
import { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useCuratedExamples(module) {
  const [examples, setExamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refetchTick, setRefetchTick] = useState(0);

  useEffect(() => {
    if (!module) return;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('access_token') || localStorage.getItem('token');
        const r = await axios.get(`${API}/curated-examples`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (cancelled) return;
        setExamples(r.data?.[module] || []);
      } catch {
        // Silent — the bar simply hides when there's nothing to show.
        if (!cancelled) setExamples([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [module, refetchTick]);

  // Lightweight refetch trigger — page consumers call this after an
  // admin saves an inline curated update so the bar reflects the new
  // values immediately (no full page reload).
  const refetch = () => setRefetchTick((n) => n + 1);

  return { examples, loading, refetch };
}
